import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/browser';
import posthog from 'posthog-js';
import { useLocation } from 'react-router';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import type {
  ConceptReplyCopyEvent,
  ConceptReplyRating,
} from '@/features/compose/components/ConceptReplyResult';
import useGenerateConceptReply from '@/features/compose/mutations/useGenerateConceptReply';
import type {
  ConceptReply,
  ConceptReplyRequest,
} from '@/features/compose/types';

export default function useConceptReply() {
  const location = useLocation();
  const initialRequest: ConceptReplyRequest | null = location.state;

  const {
    email,
    analysis: { analysis, email: analyzedEmail },
  } = useEmail();
  const messageId = email?.messageId ?? '';
  const emailId = analysis?.email_id ?? analyzedEmail?.id ?? '';

  const { mutateAsync: generateConceptReply } = useGenerateConceptReply();
  const [concept, setConcept] = useState<ConceptReply | undefined>(undefined);
  const lastRequestRef = useRef<ConceptReplyRequest | undefined>(undefined);

  const generate = useCallback(
    async (request: ConceptReplyRequest) => {
      lastRequestRef.current = request;
      setConcept({ status: 'generating' });

      const startedAt = performance.now();
      const eventProps = {
        emailId,
        messageId,
        source: request.source,
        actionCount: request.actions?.length ?? 0,
      };

      posthog.capture('Started concept reply generation', eventProps);

      try {
        const data = await generateConceptReply({
          emailId,
          actions: request.actions,
          body: request.currentConceptReply,
          prompt: request.prompt,
        });

        setConcept({ status: 'ready', text: data.text });

        posthog.capture('Completed concept reply generation', {
          ...eventProps,
          durationMs: Math.round(performance.now() - startedAt),
        });
      } catch (error) {
        setConcept({ status: 'error' });
        Sentry.captureException(error);

        posthog.capture('Concept reply generation failed', {
          isFailure: true,
          ...eventProps,
          durationMs: Math.round(performance.now() - startedAt),
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      }
    },
    [emailId, messageId, generateConceptReply],
  );

  const firedRequestRef = useRef<ConceptReplyRequest | undefined>(undefined);

  useEffect(() => {
    if (!initialRequest) return;
    if (firedRequestRef.current === initialRequest) return;

    firedRequestRef.current = initialRequest;

    generate(initialRequest);
  }, [initialRequest, generate]);

  const retry = () => {
    if (!lastRequestRef.current) return;

    return generate(lastRequestRef.current);
  };

  const trackCopied = ({ method, editedRatio }: ConceptReplyCopyEvent) => {
    posthog.capture('Concept reply copied', {
      emailId,
      messageId,
      source: lastRequestRef.current?.source,
      method,
      editedRatio,
    });
  };

  const trackRated = (rating: ConceptReplyRating) => {
    posthog.capture('Concept reply rated', {
      emailId,
      messageId,
      source: lastRequestRef.current?.source,
      rating,
    });
  };

  return { concept, initialRequest, retry, trackCopied, trackRated };
}
